import { NotificationName } from '../notification';

export enum ContestStatus {
  Published = 'published',
  Deleted = 'deleted',
  Draft = 'draft',
}

export enum ContestStatusText {
  MY_CONTEST_NOW = 'my_contest_now',
  MY_CONTEST_NEXT = 'my_contest_next',
  LIVE_CONTEST_USER_NOT_REGISTERED = 'live_contest_user_not_registered',
  FOR_YOU_NEXT = 'for_you_next',
  OTHER_CONTEST = 'other_contest', // For main part 2 contests (empty header with blue background)
  RESTRICTED = 'restricted',
}

export enum ContestStage {
  Active = 'active',
  Closed = 'closed',
  Upcoming = 'upcoming',
}

export enum ContestRegistrationStatus {
  Open = 'open',
  Closed = 'closed',
  Upcoming = 'upcoming',
}

export enum ContestFilter {
  ALL = 'all',
  CLOSEST = 'closest',
  MY_CONTESTS = 'my_contests',
}

export const BadgeColor = {
  Primary: 'primary',
  Success: 'success',
  Info: 'info',
} as const;

export const BadgeTitle = {
  Verified: 'verified',
  Profitable: 'profitable',
  Funded: 'funded',
} as const;

export enum ContestStatisticTitle {
  BestROIOfTheDay = 'best_roi_of_the_day',
  MostTradesInADay = 'most_trades_in_a_day',
  BestDealOfTheDay = 'best_deal_of_the_day',
  TotalTrades = 'total_trades',
  MostTradedSymbol = 'most_traded_symbol',
  HighestROI = 'highest_roi',
  MostProfitableTradeOfWeek = 'most_profitable_trade_of_week',
  OverallWinRate = 'overall_win_rate',
  LowestDrawdown = 'lowest_drawdown',
  ROITrendByDay = 'roi_trend_by_day',
  BiggestSingleDayGain = 'biggest_single_day_gain',
  MostProfitableTrade = 'most_profitable_trade',
  MostTradesSymbolInADay = 'most_trades_symbol_in_a_day',
}

export enum StatisticsType {
  MostTradesInADay = 'mostTradesInADay',
  TotalTrades = 'totalTrades',
  MostTradedSymbol = 'mostTradedSymbol',
  MostTradesSymbolInADay = 'mostTradesSymbolInADay',
  HighestROI = 'highestROI',
  BestROIInADay = 'bestROIInADay',
  MostProfitableTrade = 'mostProfitableTrade',
  MostProfitableTradeOfWeek = 'mostProfitableTradeOfWeek',
  BiggestSingleDayGain = 'biggestSingleDayGain',
  OverallWinRate = 'overallWinRate',
  LowestDrawdown = 'lowestDrawdown',
  ROITrendByDay = 'roiTrendByDay',
}

export const StatisticsTypeToSloganTitle: Record<
  StatisticsType,
  ContestStatisticTitle
> = {
  [StatisticsType.BestROIInADay]: ContestStatisticTitle.BestROIOfTheDay,
  [StatisticsType.MostTradesInADay]: ContestStatisticTitle.MostTradesInADay,
  [StatisticsType.MostTradesSymbolInADay]:
    ContestStatisticTitle.MostTradesSymbolInADay,
  [StatisticsType.MostProfitableTrade]:
    ContestStatisticTitle.MostProfitableTrade,
  [StatisticsType.BiggestSingleDayGain]:
    ContestStatisticTitle.BiggestSingleDayGain,
  [StatisticsType.TotalTrades]: ContestStatisticTitle.TotalTrades,
  [StatisticsType.MostTradedSymbol]: ContestStatisticTitle.MostTradedSymbol,
  [StatisticsType.HighestROI]: ContestStatisticTitle.HighestROI,
  [StatisticsType.MostProfitableTradeOfWeek]:
    ContestStatisticTitle.MostProfitableTradeOfWeek,
  [StatisticsType.OverallWinRate]: ContestStatisticTitle.OverallWinRate,
  [StatisticsType.LowestDrawdown]: ContestStatisticTitle.LowestDrawdown,
  [StatisticsType.ROITrendByDay]: ContestStatisticTitle.ROITrendByDay,
};

export const SloganValue = {
  BestROI: 'best_roi',
  MostTrades: 'most_trades',
  Symbol: 'symbol',
  SymbolFallback: 'symbol_fallback',
  ProfitAndLoss: 'profit_and_loss',
  DailyProfitAndLoss: 'daily_profit_and_loss',
  WinRate: 'win_rate',
} as const;

export const StatisticsTypeToSloganValue: Partial<
  Record<StatisticsType, (typeof SloganValue)[keyof typeof SloganValue]>
> = {
  [StatisticsType.BestROIInADay]: SloganValue.BestROI,
  [StatisticsType.BiggestSingleDayGain]: SloganValue.DailyProfitAndLoss,
  [StatisticsType.MostProfitableTrade]: SloganValue.ProfitAndLoss,
  [StatisticsType.MostTradesInADay]: SloganValue.MostTrades,
  [StatisticsType.MostTradesSymbolInADay]: SloganValue.Symbol,
  [StatisticsType.MostProfitableTradeOfWeek]: SloganValue.ProfitAndLoss,
  [StatisticsType.OverallWinRate]: SloganValue.WinRate,
};

export type SloganValueParams = {
  roi?: string;
  total?: number;
  symbol?: string;
  profitAndLoss?: number;
  dailyProfitAndLoss?: number;
  winRate?: string;
};

export const StatisticsTypeToSloganValueParams: Partial<
  Record<StatisticsType, keyof SloganValueParams>
> = {
  [StatisticsType.BestROIInADay]: 'roi',
  [StatisticsType.MostTradesInADay]: 'total',
  [StatisticsType.MostTradesSymbolInADay]: 'symbol',
  [StatisticsType.MostProfitableTrade]: 'profitAndLoss',
  [StatisticsType.BiggestSingleDayGain]: 'dailyProfitAndLoss',
  [StatisticsType.MostProfitableTradeOfWeek]: 'profitAndLoss',
  [StatisticsType.OverallWinRate]: 'winRate',
};

export type BadgeTitleEnum = (typeof BadgeTitle)[keyof typeof BadgeTitle];

export enum ContestChallengeType {
  ProfitAndLoss = 'profitAndLoss',
}

export const contestNotificationTypes = [
  NotificationName.ContestRegistration,
  NotificationName.ContestPrize,
  NotificationName.ContestStarted,
  NotificationName.ContestLastDay,
  NotificationName.ContestEnded,
] as const;

export type ContestEventNotificationType =
  (typeof contestNotificationTypes)[number];

export type ContestEmailTemplatesType = {
  [key in ContestEventNotificationType]: string;
};

export enum ContestParticipantType {
  All = 'all',
  PurchaseUser = 'purchase-user',
}

export enum ContestCommonStatus {
  None = 'none',
  Process = 'process',
  Done = 'done',
  Failed = 'failed',
  Confirmed = 'confirmed',
}

export const contestDbIdentifier = 'contest';
